import { colorTokens } from './tokens/colors.ts'

/**
 * Kestrix Design System - Modern & Minimalist
 *
 * This file defines a clean, modern design system with minimalist principles:
 * - Generous whitespace
 * - Subtle shadows and borders
 * - Clean typography
 * - Minimal color palette
 * - Consistent spacing
 */

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const colors = {
  // Base Colors
  base: {
    KestrixWhite: '#fafafa',
    KestrixBlack: '#1D1B20',
    KestrixPrimary: '#8E58EF',
    KestrixSecondary: '#392360',
    KestrixSuccess: '#18B56A',
    KestrixWarning: '#FDB022',
    KestrixError: '#F44336',
    KestrixBackgroundBlue: '#D7ECF1',
    KestrixTextTitle: '#1D1B20',
    KestrixTextBody: '#413352',
    KestrixTextSubtitle: '#7B659F',
    KestrixTextCaption: '#B5A4C6',
    KestrixTextDisabled: '#BFBFBF',
    KestrixIconColour: '#5A5555',
    KestrixThermalMode: '#FF8A00'
  },

  // Primary Colors - Updated scale
  primary: {
    100: '#E8DEFC',
    200: '#d2bcf9',
    300: '#bb9bf5',
    400: '#a579f2',
    500: '#7736ec',
    600: '#6016e6',
    700: '#5213c3',
    800: '#430fa1',
    900: '#350c7e',
    1000: '#27095c'
  },

  // Secondary Colors - Updated scale
  secondary: {
    100: '#e7e0f4',
    200: '#cfc1e8',
    300: '#b8a2dd',
    400: '#a084d2',
    500: '#8865c7',
    600: '#7046bb',
    700: '#5e3a9e',
    800: '#4b2e7f',
    900: '#2e1c4d',
    1000: '#221539'
  },

  // Neutral Colors - Updated scale
  neutral: {
    50: '#F5F5F5',
    100: '#e3e3e3',
    200: '#cccbcb',
    300: '#b5b3b3',
    400: '#9f9c9c',
    500: '#898384',
    600: '#726c6c',
    700: '#5a5555',
    800: '#433e3f',
    900: '#2b2829',
    1000: '#151314'
  },

  // Text Colors - Updated from README
  text: {
    title: '#1D1B20' /* Primary headings */,
    body: '#2E1F3E' /* Body text, descriptions */,
    subtitle: '#5D4674' /* Subheadings, labels */,
    caption: '#6B5580' /* Small text and hits */,
    disabled: '#999999' /* Disabled text */
  },

  // Purple Grey Colors (for outline buttons)
  purpleGrey: {
    25: '#FFFCF5',
    50: '#F3F1F6',
    100: '#E3E0EA',
    200: '#CEC8D9',
    300: '#B2A8C4',
    400: '#9082AA',
    500: '#6A5B86',
    600: '#413852',
    700: '#2A2435',
    800: '#1D1924',
    900: '#18151E'
  },

  // Semantic Colors - Updated
  semantic: {
    success: {
      light: '#E8F5E8',
      main: '#45B83D',
      dark: '#0F8F4F'
    },
    warning: {
      light: '#FFFAEB',
      main: '#F79009',
      dark: '#93370D'
    },
    error: {
      light: '#FFEBEE',
      main: '#F44336',
      dark: '#D32F2F'
    },
    info: {
      light: '#E3F2FD',
      main: '#2196F3',
      dark: '#1976D2'
    }
  },

  // Background Colors
  background: {
    primary: '#fafafa',
    secondary: '#FFFFFF',
    tertiary: '#D7ECF1',
    paper: '#FFFFFF',
    overlay: 'rgba(0, 0, 0, 0.5)'
  },

  // Border Colors
  border: {
    light: '#F0F0F0',
    medium: '#E0E0E0',
    dark: '#CCCCCC',
    focus: '#8E58EF',
    divider: '#F5F5F5'
  },

  // Focus Colors (with opacity for rings)
  focus: {
    primary: 'rgba(142, 88, 239, 0.18)',
    danger: 'rgba(244, 67, 54, 0.18)',
    success: 'rgba(69, 184, 61, 0.18)',
    warning: 'rgba(247, 144, 9, 0.18)',
    info: 'rgba(33, 150, 243, 0.18)'
  },

  // Shadow Colors
  shadow: {
    light: 'rgba(0, 0, 0, 0.05)',
    medium: 'rgba(0, 0, 0, 0.1)',
    dark: 'rgba(0, 0, 0, 0.15)',
    colored: 'rgba(142, 88, 239, 0.15)'
  },

  // Thermal Colors - For thermal imaging and heat visualization
  thermal: {
    warm: {
      50: '#FFF7ED',
      100: '#FFEDD5',
      200: '#FED7AA',
      300: '#FDBA74',
      400: '#FB923C',
      500: '#F97316',
      600: '#EA580C',
      700: '#C2410C',
      800: '#9A3412',
      900: '#7C2D12'
    },
    hot: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F87171',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D'
    },
    cool: {
      50: '#F0F9FF',
      100: '#E0F2FE',
      200: '#BAE6FD',
      300: '#7DD3FC',
      400: '#38BDF8',
      500: '#0EA5E9',
      600: '#0284C7',
      700: '#0369A1',
      800: '#075985',
      900: '#0C4A6E'
    }
  },

  // EPC Rating Colors - For Energy Performance Certificate ratings
  epc: {
    A: '#2E7D32', // Dark green - Excellent
    B: '#4CAF50', // Green - Very good
    C: '#8BC34A', // Light green - Good
    D: '#FFC107', // Yellow - Average
    E: '#FF9800', // Orange - Below average
    F: '#F44336', // Red - Poor
    G: '#D32F2F', // Dark red - Very poor
    '-': '#9E9E9E' // Grey - Unknown/No data
  },

  // Annotation/Anomaly Colors - For thermal annotations chips and panels
  anomaly: {
    thermal: '#9B59B6', // Thermal anomaly - purple (KestrixPrimary)
    fixtures: '#F79009', // Fixtures/Features - orange
    obstruction: '#3A67B5' // Obstruction - blue
  },

  // Visible Annotation Colors - For 3D building component annotations
  // Colorblind-friendly, vibrant colors for easy visual differentiation
  //TODO: Update these colors based on feedback from the users
  visibleAnnotations: {
    wall: 0x0066ff, // Electric Blue - structural elements
    roof: 0xff6600, // Bright Orange - distinct from blue
    window: 0x00ff00, // Pure Green - transparency/openness
    door: 0xffff00, // Pure Yellow - entry points, high visibility
    default: 0x00ff00 // Pure Green - fallback color
  }
}

// ============================================================================
// TYPOGRAPHY - Clean and readable
// ============================================================================

export const typography = {
  fontFamily: {
    primary:
      'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: 'SF Mono, Monaco, Inconsolata, "Roboto Mono", monospace'
  },

  fontSize: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    base: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem', // 48px
    '6xl': '3.75rem' // 60px
  },

  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
  },

  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.7
  },

  letterSpacing: {
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em'
  },

  // Figma heading scale (node 534:43): XXL to XXS
  heading: {
    xxlarge: {
      fontSize: '2rem', // 32px
      lineHeight: '36px',
      letterSpacing: '0',
      fontWeight: 600
    },
    xlarge: {
      fontSize: '1.75rem', // 28px
      lineHeight: '32px',
      letterSpacing: '0',
      fontWeight: 600
    },
    large: {
      fontSize: '1.5rem', // 24px
      lineHeight: '28px',
      letterSpacing: '0',
      fontWeight: 600
    },
    medium: {
      fontSize: '1.25rem', // 20px
      lineHeight: '24px',
      letterSpacing: '0',
      fontWeight: 600
    },
    small: {
      fontSize: '1rem', // 16px
      lineHeight: '20px',
      letterSpacing: '0',
      fontWeight: 600
    },
    xsmall: {
      fontSize: '0.875rem', // 14px
      lineHeight: '20px',
      letterSpacing: '0',
      fontWeight: 600
    },
    xxsmall: {
      fontSize: '0.75rem', // 12px
      lineHeight: '16px',
      letterSpacing: '0',
      fontWeight: 600
    }
  },

  // Figma body scale (node 534:59): L, M, S
  body: {
    large: {
      fontSize: '1rem', // 16px
      lineHeight: '24px',
      letterSpacing: '0',
      fontWeight: 400
    },
    medium: {
      fontSize: '0.875rem', // 14px
      lineHeight: '20px',
      letterSpacing: '0',
      fontWeight: 400
    },
    small: {
      fontSize: '0.75rem', // 12px
      lineHeight: '16px',
      letterSpacing: '0',
      fontWeight: 400
    }
  },

  // Figma metric scale (node 535:8): L, M, S
  metric: {
    large: {
      fontSize: '1.75rem', // 28px
      lineHeight: '32px',
      letterSpacing: '0',
      fontWeight: 600
    },
    medium: {
      fontSize: '1.5rem', // 24px
      lineHeight: '28px',
      letterSpacing: '0',
      fontWeight: 600
    },
    small: {
      fontSize: '1rem', // 16px
      lineHeight: '20px',
      letterSpacing: '0',
      fontWeight: 600
    }
  }
}

// ============================================================================
// SPACING - Generous and consistent
// ============================================================================

export const spacing = {
  0: '0',
  1: '0.25rem', // 4px
  2: '0.5rem', // 8px
  3: '0.75rem', // 12px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  8: '2rem', // 32px
  10: '2.5rem', // 40px
  12: '3rem', // 48px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
  32: '8rem', // 128px
  40: '10rem', // 160px
  48: '12rem', // 192px
  56: '14rem', // 224px
  64: '16rem' // 256px
}

// ============================================================================
// BORDER RADIUS - Subtle and modern
// ============================================================================

export const borderRadius = {
  none: '0',
  sm: '0.25rem', // 4px
  base: '0.375rem', // 6px
  md: '0.5rem', // 8px
  lg: '0.75rem', // 12px
  xl: '1rem', // 16px
  '2xl': '1.5rem', // 24px
  '3xl': '2rem', // 32px
  full: '9999px'
}

// ============================================================================
// SHADOWS - Subtle depth
// ============================================================================

export const shadows = {
  none: 'none',
  sm: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  base: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  md: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  lg: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  xl: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  '2xl':
    'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  focus: `0 0 0 3px ${colors.primary[200]}`,
  colored: `0 4px 12px ${colors.shadow.colored}`,
  // New consistent shadow for the platform
  consistent:
    'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  /** Lift on a raised segmented tab (Figma Shadows/lg). */
  raisedSegment: '0 2px 4px #0000000a, 0 40px 80px -16px #00000029',
  // Shadow used by dropdown menus and dropdown-like popovers
  dropdown: '0 10px 24px rgba(0, 0, 0, 0.1)',
  /** Right-edge shadow for horizontally pinned table columns. */
  tablePinnedColumn: `4px 0 8px -4px ${colors.shadow.medium}`
}

// ============================================================================
// TRANSITIONS - Smooth and natural
// ============================================================================

export const transitions = {
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  base: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  slower: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
  bounce: '200ms cubic-bezier(0.68, -0.55, 0.265, 1.55)'
}

// ============================================================================
// COMPONENT TOKENS - Modern specifications
// ============================================================================

export const componentTokens = {
  // Button Tokens
  button: {
    height: {
      xs: '24px',
      sm: '32px',
      md: '32px',
      lg: '32px',
      xl: '32px'
    },
    padding: {
      xs: '0 12px',
      sm: '0 12px',
      md: '0 12px',
      lg: '0 12px',
      xl: '0 12px'
    },
    fontSize: {
      xs: typography.fontSize.sm,
      sm: typography.fontSize.sm,
      md: typography.fontSize.sm,
      lg: typography.fontSize.sm,
      xl: typography.fontSize.sm
    },
    borderRadius: borderRadius.sm,
    fontWeight: typography.fontWeight.medium
  },

  // Input Tokens
  input: {
    height: {
      xs: '32px',
      sm: '32px',
      md: '44px',
      lg: '52px',
      xl: '60px'
    },
    padding: {
      xs: '6px 10px',
      sm: '8px 12px',
      md: '12px 16px',
      lg: '16px 20px',
      xl: '20px 24px'
    },
    fontSize: {
      xs: typography.fontSize.xs,
      sm: typography.fontSize.sm,
      md: typography.fontSize.base,
      lg: typography.fontSize.lg,
      xl: typography.fontSize.xl
    },
    borderRadius: borderRadius.base
  },

  // Card Tokens
  card: {
    padding: spacing[6],
    borderRadius: borderRadius.lg,
    shadow: shadows.base,
    border: `1px solid ${colors.border.light}`
  },

  // Modal Tokens
  modal: {
    borderRadius: borderRadius.xl,
    shadow: shadows['2xl'],
    backdrop: colors.background.overlay
  },

  // Chip Tokens
  chip: {
    height: {
      xs: '20px',
      sm: '24px',
      md: '32px',
      lg: '40px',
      xl: '48px'
    },
    padding: {
      xs: '2px 6px',
      sm: '4px 8px',
      md: '6px 12px',
      lg: '8px 16px',
      xl: '10px 20px'
    },
    borderRadius: borderRadius.full,
    fontSize: typography.fontSize.sm
  },

  // View Tab Tokens (KDS folder tabs)
  viewTab: {
    height: '32px',
    padding: '6px 10px',
    gap: '6px',
    icon: 16,
    fontSize: typography.body.small.fontSize,
    radius: {
      tabTop: borderRadius.base,
      activeTabTop: borderRadius.md
    },
    menu: {
      button: 18,
      buttonRadius: borderRadius.sm,
      buttonInset: spacing[2],
      fontSize: typography.fontSize.xs,
      paddingX: '10px',
      offset: '6px',
      minWidth: '178px',
      panelPaddingY: spacing[1],
      dividerMarginX: spacing[2],
      item: {
        icon: 16,
        gap: spacing[3],
        paddingX: spacing[3],
        paddingY: '7px'
      }
    }
  }
}

// ============================================================================
// THEME OBJECT
// ============================================================================

export const theme = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  componentTokens
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get color with opacity
 * @param {string} color - Hex color
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} Color with opacity
 */
export const withOpacity = (color, opacity) => {
  const hex = color.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * Get semantic color based on status
 * @param {string} status - Status type ('success', 'warning', 'error', 'info')
 * @returns {Object} Color object with light, main, and dark variants
 */
export const getSemanticColor = (status) => {
  return colors.semantic[status] || colors.semantic.info
}

/**
 * Get button variant colors with modern styling
 * @param {string} variant - Button variant
 * @returns {Object} Color object for button variant
 */
export const getButtonColors = (variant) => {
  const primaryState = colorTokens.buttonStates.primary
  const secondaryState = colorTokens.buttonStates.secondary
  const subtleState = colorTokens.buttonStates.subtle
  const warningState = colorTokens.buttonStates.warning
  const dangerState = colorTokens.buttonStates.danger
  const brandPrimary = colorTokens.semantic.brand.primary

  switch (variant) {
    case 'primary':
      return {
        background: primaryState.default.surface,
        color: primaryState.default.text,
        border: primaryState.default.surface,
        hover: primaryState.hover.surface,
        hoverBorder: primaryState.hover.surface,
        hoverColor: primaryState.hover.text,
        pressed: primaryState.pressed.surface,
        pressedBorder: primaryState.pressed.surface,
        pressedColor: primaryState.pressed.text,
        focusRing: brandPrimary,
        shadow: shadows.none,
        disabled: {
          background: primaryState.disabled.surface,
          color: primaryState.disabled.text,
          border: primaryState.disabled.surface
        }
      }
    case 'default':
      return {
        background: secondaryState.default.surface,
        color: secondaryState.default.text,
        border: secondaryState.default.border,
        hover: secondaryState.hover.surface,
        hoverBorder: secondaryState.hover.border,
        hoverColor: secondaryState.hover.text,
        pressed: secondaryState.pressed.surface,
        pressedBorder: secondaryState.pressed.border,
        pressedColor: secondaryState.pressed.text,
        focusRing: brandPrimary,
        shadow: shadows.none,
        disabled: {
          background: secondaryState.disabled.surface,
          color: secondaryState.disabled.text,
          border: secondaryState.disabled.border
        }
      }
    case 'subtle':
      return {
        background: subtleState.default.surface,
        color: subtleState.default.text,
        border: 'transparent',
        hover: subtleState.hover.surface,
        hoverColor: subtleState.hover.text,
        pressed: subtleState.pressed.surface,
        pressedColor: subtleState.pressed.text,
        focusRing: subtleState.focused.focusRing,
        shadow: shadows.none,
        disabled: {
          background: subtleState.disabled.surface,
          color: subtleState.disabled.text,
          border: 'transparent'
        }
      }
    case 'dashed':
      return {
        background: colors.base.KestrixWhite,
        color: colors.text.title,
        hover: {
          background: colors.base.KestrixWhite,
          color: colors.base.KestrixPrimary,
          border: colors.base.KestrixPrimary
        },
        border: colors.neutral[300],
        borderStyle: 'dashed',
        shadow: shadows.sm,
        disabled: {
          background: colors.neutral[50],
          color: colors.text.disabled,
          border: colors.border.medium
        }
      }
    case 'text':
      return {
        background: 'transparent',
        color: colors.text.title,
        hover: colors.neutral[100],
        border: 'transparent',
        shadow: shadows.none,
        disabled: {
          background: 'transparent',
          color: colors.text.disabled,
          border: 'transparent'
        }
      }
    case 'link':
      return {
        background: 'transparent',
        color: colors.base.KestrixPrimary,
        hover: 'transparent',
        border: 'transparent',
        shadow: shadows.none,
        textDecoration: 'underline',
        disabled: {
          background: 'transparent',
          color: colors.text.disabled,
          border: 'transparent'
        }
      }
    case 'danger':
      return {
        background: dangerState.default.surface,
        color: dangerState.default.text,
        border: dangerState.default.surface,
        hover: dangerState.hover.surface,
        hoverBorder: dangerState.hover.surface,
        hoverColor: dangerState.hover.text,
        pressed: dangerState.pressed.surface,
        pressedBorder: dangerState.pressed.surface,
        pressedColor: dangerState.pressed.text,
        focusRing: dangerState.focused.focusRing,
        shadow: shadows.none,
        disabled: {
          background: dangerState.disabled.surface,
          color: dangerState.disabled.text,
          border: dangerState.disabled.surface
        }
      }
    case 'warning':
      return {
        background: warningState.default.surface,
        color: warningState.default.text,
        border: warningState.default.surface,
        hover: warningState.hover.surface,
        hoverBorder: warningState.hover.surface,
        hoverColor: warningState.hover.text,
        pressed: warningState.pressed.surface,
        pressedBorder: warningState.pressed.surface,
        pressedColor: warningState.pressed.text,
        focusRing: warningState.focused.focusRing,
        shadow: shadows.none,
        disabled: {
          background: warningState.disabled.surface,
          color: warningState.disabled.text,
          border: warningState.disabled.surface
        }
      }
    default:
      return getButtonColors('primary')
  }
}

/**
 * Get toggle button style for view mode switches
 * @param {string} mode - The mode ('thermal', 'normal', etc.)
 * @param {string} currentViewMode - The currently active view mode
 * @returns {Object} Style object for toggle button
 */
export const getToggleButtonStyle = (mode, currentViewMode) => ({
  width: '48px',
  height: '30px',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    currentViewMode === mode
      ? mode === 'thermal'
        ? colors.base.KestrixThermalMode
        : colors.base.KestrixPrimary
      : 'white',
  color:
    currentViewMode === mode
      ? colors.base.KestrixPrimary
      : colors.base.KestrixIconColour,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
})

/**
 * Get tool button style for 3D viewer toolbar tools
 * @param {string} tool - The tool identifier
 * @param {string} selectedTool - The currently selected tool
 * @param {string} viewMode - The current view mode ('thermal', 'normal', etc.)
 * @returns {Object} Style object for tool button
 */
export const getToolButtonStyle = (tool, selectedTool, viewMode) => {
  const isSelected = selectedTool === tool
  const isThermal = tool.startsWith('thermal_')
  const isMeasurement = [
    'home',
    'roof',
    'wall',
    'window',
    'door',
    'features',
    'heatpump'
  ].includes(tool)
  const isEye = tool === 'eye'

  if (isThermal) {
    return {
      width: '32px',
      height: '32px',
      border: isSelected
        ? `2px solid ${colors.base.KestrixThermalMode}`
        : '2px solid transparent',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      padding: '0',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: 'none'
    }
  } else if (isMeasurement) {
    return {
      width: '40px',
      height: '40px',
      border: isSelected
        ? `2px solid ${viewMode === 'thermal' ? colors.base.KestrixThermalMode : colors.base.KestrixPrimary}`
        : 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      color: isSelected
        ? viewMode === 'thermal'
          ? colors.base.KestrixThermalMode
          : colors.base.KestrixPrimary
        : colors.base.KestrixIconColour,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: 'none'
    }
  } else if (isEye) {
    return {
      width: '48px',
      height: '40px',
      border: isSelected
        ? `2px solid ${viewMode === 'thermal' ? colors.base.KestrixThermalMode : colors.base.KestrixPrimary}`
        : 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      color: isSelected
        ? viewMode === 'thermal'
          ? colors.base.KestrixThermalMode
          : colors.base.KestrixPrimary
        : colors.base.KestrixIconColour,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: 'none'
    }
  } else {
    return {
      width: '40px',
      height: '40px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isSelected ? colors.base.KestrixPrimary : 'white',
      color: isSelected ? 'white' : colors.base.KestrixIconColour,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }
  }
}

/**
 * Get anomaly type button style for 3D viewer toolbar
 * @param {string} anomalyType - The anomaly type identifier
 * @param {Set} selectedAnomalyTypes - Set of currently selected anomaly types
 * @returns {Object} Style object for anomaly type button
 */
export const getAnomalyTypeButtonStyle = (_anomalyType, _selectedAnomalyTypes) => {
  return {
    width: '54px',
    height: '54px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    padding: '0',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative'
  }
}

// ============================================================================
// ANIMATIONS
// ============================================================================

/**
 * Keyframe animations for skeleton loading states
 */
export const animations = {
  pulse: {
    '@keyframes pulse': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.5 },
      '100%': { opacity: 1 }
    }
  },
  wave: {
    '@keyframes wave': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.3 },
      '100%': { opacity: 1 }
    }
  }
}

export default theme
